import { Head } from 'vite-react-ssg';
import { AGENCY_SCHEMA } from '../lib/seoSchemas';

const BASE_URL = 'https://bigbirdtravel.com';
const SITE_NAME = 'Big Bird Travel & Tourism UAE';
const DEFAULT_OG_IMAGE = 'https://bigbirdtravel.com/og-cover.jpg';

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  /** Full URL override for OG image — defaults to site-wide cover */
  ogImage?: string;
  /** 'website' for standard pages, 'article' for blog posts */
  ogType?: 'website' | 'article';
  /** Pass one or multiple JSON-LD schema objects */
  schema?: object | object[];
  /** When true the title is used as-is; otherwise " | Big Bird Travel & Tourism UAE" is appended */
  skipSuffix?: boolean;
  /** Set true for thank-you pages, admin pages, etc. */
  noindex?: boolean;
  /** Skip the auto-included AGENCY_SCHEMA (rarely needed) */
  skipAgencySchema?: boolean;
}

export default function SEO({
  title,
  description,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
  schema,
  skipSuffix = false,
  noindex = false,
  skipAgencySchema = false,
}: SEOProps) {
  const fullTitle = skipSuffix ? title : `${title} | Big Bird Travel`;
  // Keep meta descriptions within Google's ~160-char snippet: trim at a word
  // boundary (no mid-word cuts) so no page renders a truncated-looking snippet.
  const metaDescription = description.length <= 160
    ? description
    : (() => {
        const cut = description.slice(0, 157);
        const sp = cut.lastIndexOf(' ');
        return (sp > 120 ? cut.slice(0, sp) : cut).replace(/[\s,;:.\-–—]+$/, '') + '…';
      })();
  const canonicalHref = canonical
    ? (() => {
        const url = canonical.startsWith('http') ? canonical : `${BASE_URL}${canonical}`;
        // Add trailing slash to match Hostinger's 301 redirect behaviour.
        // Without this, canonical (/visas) points to a URL that 301s to /visas/,
        // creating a redirect loop that prevents Google from indexing the page.
        return url.endsWith('/') || url.includes('?') || url.includes('#') ? url : `${url}/`;
      })()
    : '';
  const robots = noindex
    ? 'noindex, nofollow'
    : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1';
  const pageSchemas = schema ? (Array.isArray(schema) ? schema : [schema]) : [];
  // Auto-include AGENCY_SCHEMA on every page (deduped if the page already passes it)
  const alreadyHasAgency = pageSchemas.some((s: { '@type'?: unknown }) => {
    const t = s['@type'];
    if (Array.isArray(t)) return t.includes('TravelAgency') || t.includes('LocalBusiness');
    return t === 'TravelAgency' || t === 'LocalBusiness';
  });
  const schemas = !skipAgencySchema && !alreadyHasAgency
    ? [AGENCY_SCHEMA, ...pageSchemas]
    : pageSchemas;

  return (
    <Head>
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="robots" content={robots} />
      {canonicalHref && <link rel="canonical" href={canonicalHref} />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_AE" />
      {canonicalHref && <meta property="og:url" content={canonicalHref} />}

      {/* Twitter / X Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD schemas */}
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Head>
  );
}
